'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  HAS_CUSTOM_FOCUS_CLASSNAME,
  getDataAttributes,
  getAccessibilityAttributes,
} from '@wix/editor-elements-common-utils';
import type {
  ITextInputImperativeActions,
  ITextInputProps,
} from '../TextInput.types';
import { InlineErrorMessage } from '../../../core/inlineErrorMessage';
import semanticClassNames from '../TextInput.semanticClassNames';
import style from './style/TextInput.scss';
import {
  formatPhoneNumber,
  getUnformattedNumber,
  hasNonNumericChar,
  translateHtmlValidationMessage,
} from './utils';

const noop = () => {};

const TextInput: React.ForwardRefRenderFunction<
  ITextInputImperativeActions,
  ITextInputProps
> = (props, ref) => {
  const {
    skin,
    name,
    id,
    className = '',
    customClassNames = [],
    dataTestId,
    value,
    inputType = 'text',
    label,
    placeholder,
    readOnly,
    required,
    isDisabled,
    pattern,
    autoComplete,
    autoComplete_,
    maxLength,
    min,
    max,
    step,
    numberSpinnerHidden,
    shouldShowValidityIndication,
    autoFocus,
    prefix,
    phoneFormat = '',
    errorMessageType = 'tooltip',
    validateValue = noop,
    setValidityIndication = noop,
    onBlur = noop,
    onFocus = noop,
    onKeyPress = noop,
    onInput = noop,
    onValueChange = noop,
    onChange = noop,
    onClick = noop,
    onDblClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onHydrationComplete = noop,
    ariaAttributes,
    contentRef,
    translations,
    componentViewMode,
    textInputAutoFillFixEnabled,
    lang,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
      setCustomValidity: message => {
        if (message.type === 'message') {
          inputRef.current?.setCustomValidity(message.message);
        } else {
          inputRef.current?.setCustomValidity(
            translateHtmlValidationMessage(message, {
              translations: props.translations,
              phoneFormat: props.phoneFormat,
            }),
          );
        }
      },
      getValidationMessage: () => {
        return inputRef.current?.validationMessage;
      },
    };
  });

  const valueChanged = React.useRef<boolean>();

  const formattedValue = React.useMemo(() => {
    return inputType === 'tel' && phoneFormat
      ? formatPhoneNumber(value, phoneFormat)
      : value;
  }, [value, phoneFormat, inputType]);

  const handleAutoFillChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (textInputAutoFillFixEnabled && !hasFocus()) {
      onChange({
        ...event,
        type: 'change',
      });
    }
  };

  const getOnChangeFn =
    ({
      shouldHideValidityIndication,
      shouldCallOnInput,
    }: {
      shouldHideValidityIndication: boolean;
      shouldCallOnInput: boolean;
    }) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: eventValue } = event.target;
      let unformattedNumber = eventValue;

      if (phoneFormat) {
        const didDeleteChars = eventValue.length < formattedValue.length;

        unformattedNumber = getUnformattedNumber(
          eventValue,
          phoneFormat,
          didDeleteChars,
        );

        if (hasNonNumericChar(unformattedNumber)) {
          return;
        }
      }
      const shouldUpdateValue = unformattedNumber !== value;
      if (shouldUpdateValue) {
        valueChanged.current = true;
        onValueChange(unformattedNumber);
        unformattedNumber !== value && handleAutoFillChange(event);
        validateValue();
      }
      if (shouldHideValidityIndication) {
        setValidityIndication(false);
      }
      if (shouldCallOnInput) {
        onInput(event);
      }
    };

  React.useEffect(() => {
    const onChangeFn = getOnChangeFn({
      shouldHideValidityIndication: true, // ECL-8372
      shouldCallOnInput: false,
    });
    onChangeFn({
      target: { value: inputRef.current?.value ?? '' },
    } as React.ChangeEvent<HTMLInputElement>);
    onHydrationComplete({ type: 'onHydrationComplete' });
    // We want to run this useEffect only once after SSR has done loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
    onBlur(event);
    if (valueChanged.current) {
      onChange({
        ...event,
        type: 'change',
      });
    }
    valueChanged.current = false;
    setValidityIndication(true);
  };

  const _onClick: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onClick(event);
    }
  };

  const _onDblClick: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onDblClick(event);
    }
  };

  const _onMouseEnter: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onMouseEnter(event);
    }
  };

  const _onMouseLeave: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onMouseLeave(event);
    }
  };

  const containerClasses = classNames(
    style[skin],
    className,
    formatClassNames(semanticClassNames.root, ...customClassNames),
    {
      [style.hasLabel]: !!label,
      [style.requiredIndication]: required,
      [style.validationIndication]: !!shouldShowValidityIndication,
    },
  );

  const getMaxLength = () => {
    let updatedMaxLength = maxLength === null ? undefined : maxLength;

    if (phoneFormat) {
      updatedMaxLength = phoneFormat.length;
    }

    return updatedMaxLength;
  };

  const getMinLength = () => (phoneFormat ? phoneFormat.length : undefined);

  const {
    ariaAttributes: { 'aria-label': ariaLabel, ...restAriaAttributes },
  } = getAccessibilityAttributes({ ariaAttributes });

  const hasFocus = () =>
    !!inputRef.current && inputRef.current === document.activeElement;

  const hasInvalidErrorMessage =
    !!inputRef.current?.validationMessage && shouldShowValidityIndication;

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      ref={contentRef}
      className={containerClasses}
      data-testid={dataTestId}
      onClick={_onClick}
      onDoubleClick={_onDblClick}
      onMouseEnter={_onMouseEnter}
      onMouseLeave={_onMouseLeave}
      lang={lang}
    >
      {!!label && (
        <label
          htmlFor={`input_${id}`}
          className={classNames(
            style.label,
            formatClassNames(semanticClassNames.inputLabel),
          )}
        >
          {label}
        </label>
      )}
      <div className={style.inputWrapper}>
        {prefix && <div className={style.prefix}>{prefix}</div>}
        <input
          name={name}
          ref={inputRef}
          id={`input_${id}`}
          className={classNames(
            style.input,
            HAS_CUSTOM_FOCUS_CLASSNAME,
            formatClassNames(semanticClassNames.inputEl),
            {
              [style.hideNumberSpinner]: numberSpinnerHidden,
            },
          )}
          type={inputType}
          value={formattedValue}
          onFocus={onFocus}
          onKeyDown={onKeyPress}
          onChange={getOnChangeFn({
            shouldHideValidityIndication: true,
            shouldCallOnInput: true,
          })}
          onBlur={_onBlur}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          aria-invalid={hasInvalidErrorMessage}
          pattern={pattern}
          maxLength={getMaxLength()}
          minLength={getMinLength()}
          disabled={isDisabled}
          autoComplete={autoComplete ? 'on' : autoComplete_ || 'off'}
          step={step === null ? undefined : step}
          min={min === null ? undefined : min}
          max={max === null ? undefined : max}
          autoFocus={autoFocus}
          {...(ariaLabel && { 'aria-label': ariaLabel })}
          {...(!required && { 'aria-required': false })}
          {...restAriaAttributes}
        />
      </div>
      <InlineErrorMessage
        errorMessageType={errorMessageType}
        errorMessage={inputRef.current?.validationMessage}
        shouldShowValidityIndication={shouldShowValidityIndication}
        translations={translations}
        componentViewMode={componentViewMode}
      />
    </div>
  );
};

export default React.forwardRef(TextInput);
